'use client';

import { c as _c } from "react/compiler-runtime";
import useEmblaCarousel from 'embla-carousel-react';
import { type ImageProps } from 'next/image';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from '@/app/components/button';
import { IconAdd } from '@/app/components/icons/ui-actions/icon-add';
import { IconChevronLeft } from '@/app/components/icons/ui-actions/icon-chevron-left';
import { IconChevronRight } from '@/app/components/icons/ui-actions/icon-chevron-right';
import { IconClose } from '@/app/components/icons/ui-actions/icon-close';
import { IconDownload } from '@/app/components/icons/ui-actions/icon-download';
import { IconRemove } from '@/app/components/icons/ui-actions/icon-remove';
import { IconResetZoom } from '@/app/components/icons/ui-actions/icon-reset-zoom';
import { ImageZoomPanPinch } from '@/app/components/image-zoom-pan-pinch';
import { useImageZoomPanPinch } from '@/app/components/image-zoom-pan-pinch/useImageZoomPanPinch';
import { Container } from '@/app/components/layouts/container';
import type { OverlayKey } from '@/app/components/overlays/core/types';
import { useOverlay } from '@/app/components/overlays/hooks/useOverlay';
import { Overlay } from '@/app/components/overlays/overlay';
import { useMountTransition } from '@/app/hooks/useMountTransition';
import { cn } from '@/app/utils/cn';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { downloadFile } from '@/app/utils/downloadFile';
const clampIndex = (index: number, length: number) => {
  if (length <= 0) return 0;
  return Math.max(0, Math.min(index, length - 1));
};
const normalizeDim = (val?: number | `${number}` | null): number => {
  const num = typeof val === 'string' ? Number(val) : val;
  return Number.isFinite(num) && (num ?? 0) > 0 ? num as number : 1;
};
const sanitizeForTestId = (value: string) => value.replace(/[^a-zA-Z0-9-_]/g, '-');
export interface GalleryZoomModalProps {
  images: Pick<ImageProps, 'src' | 'alt' | 'width' | 'height'>[];
  initialIndex: number;
  overlayKey: OverlayKey;
  instanceId: string;
  testId?: string;
}
export const GalleryZoomModal = t0 => {
  const $ = _c(177);
  const {
    images,
    initialIndex,
    overlayKey,
    instanceId,
    testId: t1
  } = t0;
  const testId = t1 === undefined ? "gallery-zoom-modal" : t1;
  let t2;
  if ($[0] !== instanceId || $[1] !== overlayKey) {
    t2 = {
      overlayKey,
      instanceId
    };
    $[0] = instanceId;
    $[1] = overlayKey;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    isOpen,
    overlayProps
  } = useOverlay(t2);
  const {
    onClose: overlayOnClose
  } = overlayProps;
  const contentRef = useRef(null);
  const actionsTopRef = useRef(null);
  const actionsBottomRef = useRef(null);
  const clickOutsideRef = useRef(null);
  const allowEmblaSwipeRef = useRef(true);
  let t3;
  if ($[3] !== images.length || $[4] !== initialIndex) {
    t3 = () => clampIndex(initialIndex, images.length);
    $[3] = images.length;
    $[4] = initialIndex;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const [activeIndex, setActiveIndex] = useState(t3);
  const current = images[activeIndex];
  const src = current?.src ? `${current.src}` : "";
  const alt = current?.alt ?? "";
  const t4 = current?.width;
  let t5;
  if ($[6] !== t4) {
    t5 = normalizeDim(t4);
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const normalizedWidth = t5;
  const t6 = current?.height;
  let t7;
  if ($[8] !== t6) {
    t7 = normalizeDim(t6);
    $[8] = t6;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const normalizedHeight = t7;
  let t8;
  if ($[10] !== isOpen) {
    t8 = {
      isMount: isOpen,
      ref: contentRef,
      baseClassName: "opacity-0",
      animateClassName: "opacity-100",
      exitClassName: "opacity-0"
    };
    $[10] = isOpen;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    transitionState
  } = useMountTransition(t8);
  const shouldRenderOverlay = (isOpen || transitionState === "exit") && images.length > 0;
  const t9 = `${testId}-image-${activeIndex}`;
  let t10;
  if ($[12] !== t9) {
    t10 = sanitizeForTestId(t9);
    $[12] = t9;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  const imageDataTestId = t10;
  let t11;
  if ($[14] !== images.length || $[15] !== initialIndex) {
    t11 = clampIndex(initialIndex, images.length);
    $[14] = images.length;
    $[15] = initialIndex;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => allowEmblaSwipeRef.current;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== t11) {
    t13 = {
      startIndex: t11,
      watchDrag: t12,
      containScroll: false
    };
    $[18] = t11;
    $[19] = t13;
  } else {
    t13 = $[19];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t13);
  const t14 = `${activeIndex}-${src}`;
  let t15;
  if ($[20] !== imageDataTestId || $[21] !== normalizedHeight || $[22] !== normalizedWidth || $[23] !== shouldRenderOverlay || $[24] !== t14) {
    t15 = {
      normalizedWidth,
      normalizedHeight,
      imageDataTestId,
      shouldRender: shouldRenderOverlay,
      actionsTopRef,
      actionsBottomRef,
      stageLayoutKey: t14,
      onClickOutsideImageRef: clickOutsideRef
    };
    $[20] = imageDataTestId;
    $[21] = normalizedHeight;
    $[22] = normalizedWidth;
    $[23] = shouldRenderOverlay;
    $[24] = t14;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const zoom = useImageZoomPanPinch(t15);
  const {
    resetScaleImmediately,
    handleStageClickCapture,
    isResetDisabled
  } = zoom;
  let t16;
  if ($[26] !== overlayOnClose || $[27] !== resetScaleImmediately) {
    t16 = () => {
      resetScaleImmediately();
      overlayOnClose();
    };
    $[26] = overlayOnClose;
    $[27] = resetScaleImmediately;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  const handleClose = t16;
  let t17;
  let t18;
  if ($[29] !== isResetDisabled) {
    t17 = () => {
      allowEmblaSwipeRef.current = isResetDisabled;
    };
    t18 = [isResetDisabled];
    $[29] = isResetDisabled;
    $[30] = t17;
    $[31] = t18;
  } else {
    t17 = $[30];
    t18 = $[31];
  }
  useEffect(t17, t18);
  let t19;
  let t20;
  if ($[32] !== handleClose) {
    t19 = () => {
      clickOutsideRef.current = handleClose;
    };
    t20 = [handleClose];
    $[32] = handleClose;
    $[33] = t19;
    $[34] = t20;
  } else {
    t19 = $[33];
    t20 = $[34];
  }
  useEffect(t19, t20);
  let t21;
  if ($[35] !== alt || $[36] !== src) {
    t21 = async () => {
      if (!src) {
        return;
      }
      await downloadFile({
        url: src,
        name: alt || "gallery-image"
      });
    };
    $[35] = alt;
    $[36] = src;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  const handleDownload = t21;
  let t22;
  if ($[38] !== emblaApi) {
    t22 = emblaApi?.canScrollPrev() ?? false;
    $[38] = emblaApi;
    $[39] = t22;
  } else {
    t22 = $[39];
  }
  const canGoPrev = t22;
  let t23;
  if ($[40] !== emblaApi) {
    t23 = emblaApi?.canScrollNext() ?? false;
    $[40] = emblaApi;
    $[41] = t23;
  } else {
    t23 = $[41];
  }
  const canGoNext = t23;
  const showNav = images.length > 1;
  let t24;
  let t25;
  if ($[42] !== emblaApi || $[43] !== resetScaleImmediately) {
    t24 = () => {
      if (!emblaApi) {
        return;
      }
      const onSelect = () => {
        setActiveIndex(emblaApi.selectedScrollSnap());
        resetScaleImmediately();
      };
      emblaApi.on("select", onSelect);
      return () => {
        emblaApi.off("select", onSelect);
      };
    };
    t25 = [emblaApi, resetScaleImmediately];
    $[42] = emblaApi;
    $[43] = resetScaleImmediately;
    $[44] = t24;
    $[45] = t25;
  } else {
    t24 = $[44];
    t25 = $[45];
  }
  useEffect(t24, t25);
  let t26;
  let t27;
  if ($[46] !== emblaApi || $[47] !== images.length || $[48] !== initialIndex || $[49] !== isOpen || $[50] !== resetScaleImmediately) {
    t26 = () => {
      if (!isOpen || !emblaApi) {
        return;
      }
      const idx = clampIndex(initialIndex, images.length);
      emblaApi.scrollTo(idx, true);
      resetScaleImmediately();
      queueMicrotask(() => setActiveIndex(idx));
    };
    t27 = [emblaApi, images.length, initialIndex, isOpen, resetScaleImmediately];
    $[46] = emblaApi;
    $[47] = images.length;
    $[48] = initialIndex;
    $[49] = isOpen;
    $[50] = resetScaleImmediately;
    $[51] = t26;
    $[52] = t27;
  } else {
    t26 = $[51];
    t27 = $[52];
  }
  useEffect(t26, t27);
  let t28;
  let t29;
  if ($[53] !== emblaApi || $[54] !== isOpen || $[55] !== showNav) {
    t28 = () => {
      if (!isOpen || !showNav) {
        return;
      }
      const onKeyDown = event => {
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
          }
          emblaApi?.scrollPrev();
        } else {
          if (event.key === "ArrowRight") {
            event.preventDefault();
            if (document.activeElement instanceof HTMLElement) {
              document.activeElement.blur();
            }
            emblaApi?.scrollNext();
          }
        }
      };
      window.addEventListener("keydown", onKeyDown);
      return () => window.removeEventListener("keydown", onKeyDown);
    };
    t29 = [emblaApi, isOpen, showNav];
    $[53] = emblaApi;
    $[54] = isOpen;
    $[55] = showNav;
    $[56] = t28;
    $[57] = t29;
  } else {
    t28 = $[56];
    t29 = $[57];
  }
  useEffect(t28, t29);
  if (!shouldRenderOverlay) {
    return null;
  }
  let t30;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = cn("flex flex-col items-center");
    $[58] = t30;
  } else {
    t30 = $[58];
  }
  let t31;
  if ($[59] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = cn("pointer-events-none relative z-10 w-full p-4", "flex items-center justify-end gap-2", "transition-opacity");
    $[59] = t31;
  } else {
    t31 = $[59];
  }
  const t32 = `${testId}-actions-top`;
  let t33;
  if ($[60] !== t32) {
    t33 = createDataAttributes({
      test: t32
    });
    $[60] = t32;
    $[61] = t33;
  } else {
    t33 = $[61];
  }
  let t34;
  if ($[62] !== activeIndex || $[63] !== images.length || $[64] !== showNav || $[65] !== testId) {
    t34 = !!showNav && <div className="text-body-1-fixed rounded-6 center-x center-y pointer-events-none absolute flex h-10 items-center gap-x-1 rounded-sm bg-black/70 px-4 text-center text-white" {...createDataAttributes({
      test: `${testId}-image-counter`
    })}><span className="w-8">{activeIndex + 1}</span><span>/</span> <span className="w-8">{images.length}</span></div>;
    $[62] = activeIndex;
    $[63] = images.length;
    $[64] = showNav;
    $[65] = testId;
    $[66] = t34;
  } else {
    t34 = $[66];
  }
  let t35;
  if ($[67] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <IconDownload />;
    $[67] = t35;
  } else {
    t35 = $[67];
  }
  const t36 = `${testId}-download-button`;
  let t37;
  if ($[68] !== t36) {
    t37 = createDataAttributes({
      test: t36
    });
    $[68] = t36;
    $[69] = t37;
  } else {
    t37 = $[69];
  }
  let t38;
  if ($[70] !== handleDownload || $[71] !== t37) {
    t38 = <Button variant="tertiary" color="white" iconBefore={t35} className="pointer-events-auto" aria-label="Download image" onClick={handleDownload} {...t37} />;
    $[70] = handleDownload;
    $[71] = t37;
    $[72] = t38;
  } else {
    t38 = $[72];
  }
  let t39;
  if ($[73] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <IconClose />;
    $[73] = t39;
  } else {
    t39 = $[73];
  }
  const t40 = `${testId}-close-button`;
  let t41;
  if ($[74] !== t40) {
    t41 = createDataAttributes({
      test: t40
    });
    $[74] = t40;
    $[75] = t41;
  } else {
    t41 = $[75];
  }
  let t42;
  if ($[76] !== handleClose || $[77] !== t41) {
    t42 = <Button color="black" iconBefore={t39} className="pointer-events-auto" aria-label="Close image" onClick={handleClose} {...t41} />;
    $[76] = handleClose;
    $[77] = t41;
    $[78] = t42;
  } else {
    t42 = $[78];
  }
  let t43;
  if ($[79] !== t38 || $[80] !== t42) {
    t43 = <div className="flex items-center gap-2">{t38}{t42}</div>;
    $[79] = t38;
    $[80] = t42;
    $[81] = t43;
  } else {
    t43 = $[81];
  }
  let t44;
  if ($[82] !== t33 || $[83] !== t34 || $[84] !== t43) {
    t44 = <div ref={actionsTopRef} className={t31} {...t33}>{t34}{t43}</div>;
    $[82] = t33;
    $[83] = t34;
    $[84] = t43;
    $[85] = t44;
  } else {
    t44 = $[85];
  }
  const t45 = `Gallery image preview ${alt}`;
  let t46;
  if ($[86] === Symbol.for("react.memo_cache_sentinel")) {
    t46 = cn("relative flex min-h-0 w-full flex-1 outline-none");
    $[86] = t46;
  } else {
    t46 = $[86];
  }
  let t47;
  if ($[87] !== canGoPrev || $[88] !== emblaApi || $[89] !== showNav || $[90] !== testId) {
    t47 = !!showNav && <Button aria-label="Previous image" className="center-y absolute left-4 z-10 hidden shrink-0 sm:flex" color="black" iconBefore={<IconChevronLeft />} disabled={!canGoPrev} onClick={() => emblaApi?.scrollPrev()} {...createDataAttributes({
      test: `${testId}-prev-button`
    })} />;
    $[87] = canGoPrev;
    $[88] = emblaApi;
    $[89] = showNav;
    $[90] = testId;
    $[91] = t47;
  } else {
    t47 = $[91];
  }
  let t48;
  if ($[92] !== activeIndex || $[93] !== handleStageClickCapture || $[94] !== imageDataTestId || $[95] !== images || $[96] !== normalizedHeight || $[97] !== normalizedWidth || $[98] !== src || $[99] !== testId || $[100] !== zoom) {
    let t49;
    if ($[102] !== activeIndex || $[103] !== handleStageClickCapture || $[104] !== imageDataTestId || $[105] !== normalizedHeight || $[106] !== normalizedWidth || $[107] !== src || $[108] !== testId || $[109] !== zoom) {
      t49 = (image, i) => {
        const isActive = i === activeIndex;
        const slideSrc = `${image.src}`;
        const slideAlt = image.alt ?? "";
        return <div key={`${slideSrc}-${i}`} className="relative h-full min-w-0 flex-none basis-full" {...createDataAttributes({
          test: `${testId}-slide-${i}`
        })}>{!!isActive && <Container size="none" maxWidth="none" className="absolute inset-0" onClickCapture={handleStageClickCapture} {...createDataAttributes({
            test: `${testId}-content`
          })}><ImageZoomPanPinch zoom={zoom} alt={slideAlt} imageDataTestId={imageDataTestId} normalizedHeight={normalizedHeight} normalizedWidth={normalizedWidth} src={src} transformInstanceKey={`${i}-${src}`} /></Container>}</div>;
      };
      $[102] = activeIndex;
      $[103] = handleStageClickCapture;
      $[104] = imageDataTestId;
      $[105] = normalizedHeight;
      $[106] = normalizedWidth;
      $[107] = src;
      $[108] = testId;
      $[109] = zoom;
      $[110] = t49;
    } else {
      t49 = $[110];
    }
    t48 = images.map(t49);
    $[92] = activeIndex;
    $[93] = handleStageClickCapture;
    $[94] = imageDataTestId;
    $[95] = images;
    $[96] = normalizedHeight;
    $[97] = normalizedWidth;
    $[98] = src;
    $[99] = testId;
    $[100] = zoom;
    $[101] = t48;
  } else {
    t48 = $[101];
  }
  let t49;
  if ($[111] !== t48) {
    t49 = <div className="flex h-full">{t48}</div>;
    $[111] = t48;
    $[112] = t49;
  } else {
    t49 = $[112];
  }
  let t50;
  if ($[113] !== emblaRef || $[114] !== t49) {
    t50 = <div ref={emblaRef} className="size-full">{t49}</div>;
    $[113] = emblaRef;
    $[114] = t49;
    $[115] = t50;
  } else {
    t50 = $[115];
  }
  let t51;
  if ($[116] !== canGoNext || $[117] !== emblaApi || $[118] !== showNav || $[119] !== testId) {
    t51 = !!showNav && <Button aria-label="Next image" className="center-y absolute right-4 z-10 hidden shrink-0 sm:flex" color="black" disabled={!canGoNext} iconBefore={<IconChevronRight />} onClick={() => emblaApi?.scrollNext()} {...createDataAttributes({
      test: `${testId}-next-button`
    })} />;
    $[116] = canGoNext;
    $[117] = emblaApi;
    $[118] = showNav;
    $[119] = testId;
    $[120] = t51;
  } else {
    t51 = $[120];
  }
  let t52;
  if ($[121] !== t45 || $[122] !== t47 || $[123] !== t50 || $[124] !== t51) {
    t52 = <div ref={contentRef} aria-label={t45} aria-modal="true" role="dialog" className={t46}>{t47}{t50}{t51}</div>;
    $[121] = t45;
    $[122] = t47;
    $[123] = t50;
    $[124] = t51;
    $[125] = t52;
  } else {
    t52 = $[125];
  }
  let t53;
  if ($[126] === Symbol.for("react.memo_cache_sentinel")) {
    t53 = cn("pointer-events-none relative w-full p-4 pointer-coarse:hidden", "flex items-center justify-center");
    $[126] = t53;
  } else {
    t53 = $[126];
  }
  const t54 = `${testId}-actions-bottom`;
  let t55;
  if ($[127] !== t54) {
    t55 = createDataAttributes({
      test: t54
    });
    $[127] = t54;
    $[128] = t55;
  } else {
    t55 = $[128];
  }
  const t56 = `${testId}-controls`;
  let t57;
  if ($[129] !== t56) {
    t57 = createDataAttributes({
      test: t56
    });
    $[129] = t56;
    $[130] = t57;
  } else {
    t57 = $[130];
  }
  let t58;
  if ($[131] !== zoom) {
    t58 = zoom.getZoomControlButtonClassName(zoom.isZoomOutDisabled);
    $[131] = zoom;
    $[132] = t58;
  } else {
    t58 = $[132];
  }
  const t59 = zoom.isZoomOutDisabled;
  let t60;
  if ($[133] === Symbol.for("react.memo_cache_sentinel")) {
    t60 = <IconRemove />;
    $[133] = t60;
  } else {
    t60 = $[133];
  }
  const t61 = zoom.handleZoomOut;
  const t62 = `${testId}-zoom-out-button`;
  let t63;
  if ($[134] !== t62) {
    t63 = createDataAttributes({
      test: t62
    });
    $[134] = t62;
    $[135] = t63;
  } else {
    t63 = $[135];
  }
  let t64;
  if ($[136] !== t58 || $[137] !== t63 || $[138] !== zoom.handleZoomOut || $[139] !== zoom.isZoomOutDisabled) {
    t64 = <Button aria-label="Zoom out image" className={t58} color="black" disabled={t59} iconBefore={t60} onClick={t61} {...t63} />;
    $[136] = t58;
    $[137] = t63;
    $[138] = zoom.handleZoomOut;
    $[139] = zoom.isZoomOutDisabled;
    $[140] = t64;
  } else {
    t64 = $[140];
  }
  let t65;
  if ($[141] !== zoom) {
    t65 = zoom.getZoomControlButtonClassName(zoom.isResetDisabled);
    $[141] = zoom;
    $[142] = t65;
  } else {
    t65 = $[142];
  }
  const t66 = zoom.isResetDisabled;
  const t67 = zoom.handleResetZoom;
  const t68 = `${testId}-zoom-reset-button`;
  let t69;
  if ($[143] !== t68) {
    t69 = createDataAttributes({
      test: t68
    });
    $[143] = t68;
    $[144] = t69;
  } else {
    t69 = $[144];
  }
  let t70;
  if ($[145] === Symbol.for("react.memo_cache_sentinel")) {
    t70 = <IconResetZoom />;
    $[145] = t70;
  } else {
    t70 = $[145];
  }
  let t71;
  if ($[146] !== t65 || $[147] !== t69 || $[148] !== zoom.handleResetZoom || $[149] !== zoom.isResetDisabled) {
    t71 = <Button aria-label="Reset zoom image" className={t65} color="black" disabled={t66} onClick={t67} {...t69} iconBefore={t70} />;
    $[146] = t65;
    $[147] = t69;
    $[148] = zoom.handleResetZoom;
    $[149] = zoom.isResetDisabled;
    $[150] = t71;
  } else {
    t71 = $[150];
  }
  let t72;
  if ($[151] !== zoom) {
    t72 = zoom.getZoomControlButtonClassName(zoom.isZoomInDisabled);
    $[151] = zoom;
    $[152] = t72;
  } else {
    t72 = $[152];
  }
  const t73 = zoom.isZoomInDisabled;
  let t74;
  if ($[153] === Symbol.for("react.memo_cache_sentinel")) {
    t74 = <IconAdd />;
    $[153] = t74;
  } else {
    t74 = $[153];
  }
  const t75 = zoom.handleZoomIn;
  const t76 = `${testId}-zoom-in-button`;
  let t77;
  if ($[154] !== t76) {
    t77 = createDataAttributes({
      test: t76
    });
    $[154] = t76;
    $[155] = t77;
  } else {
    t77 = $[155];
  }
  let t78;
  if ($[156] !== t72 || $[157] !== t77 || $[158] !== zoom.handleZoomIn || $[159] !== zoom.isZoomInDisabled) {
    t78 = <Button aria-label="Zoom in image" className={t72} color="black" disabled={t73} iconBefore={t74} onClick={t75} {...t77} />;
    $[156] = t72;
    $[157] = t77;
    $[158] = zoom.handleZoomIn;
    $[159] = zoom.isZoomInDisabled;
    $[160] = t78;
  } else {
    t78 = $[160];
  }
  let t79;
  if ($[161] !== t57 || $[162] !== t64 || $[163] !== t71 || $[164] !== t78) {
    t79 = <div className="flex items-center justify-center gap-2 rounded-sm bg-black/70 p-2" {...t57}>{t64}{t71}{t78}</div>;
    $[161] = t57;
    $[162] = t64;
    $[163] = t71;
    $[164] = t78;
    $[165] = t79;
  } else {
    t79 = $[165];
  }
  let t80;
  if ($[166] !== t55 || $[167] !== t79) {
    t80 = <div ref={actionsBottomRef} className={t53} {...t55}>{t79}</div>;
    $[166] = t55;
    $[167] = t79;
    $[168] = t80;
  } else {
    t80 = $[168];
  }
  let t81;
  if ($[169] !== handleClose || $[170] !== isOpen || $[171] !== overlayProps || $[172] !== t44 || $[173] !== t52 || $[174] !== t80 || $[175] !== transitionState) {
    t81 = <Overlay {...overlayProps} isOpen={isOpen} onClose={handleClose} transitionState={transitionState} autoFocus={false} backdropClassName="bg-black/70" overlayClassName={t30}>{t44}{t52}{t80}</Overlay>;
    $[169] = handleClose;
    $[170] = isOpen;
    $[171] = overlayProps;
    $[172] = t44;
    $[173] = t52;
    $[174] = t80;
    $[175] = transitionState;
    $[176] = t81;
  } else {
    t81 = $[176];
  }
  return t81;
};